import { motion } from "framer-motion";
import { ArrowRight, Code2, LineChart, Puzzle, Shield, Sparkles, Bot } from "lucide-react";

const features = [
  {
    icon: Code2,
    title: "One call, brand-accurate size",
    body: "POST /api/recommend maps a shopper's FitID to the right size and width for your specific brand.",
  },
  {
    icon: LineChart,
    title: "Measured impact",
    body: "Live dashboard for return-rate delta, conversion lift, and confidence distribution across your catalog.",
  },
  {
    icon: Puzzle,
    title: "Your catalog, calibrated",
    body: "We map the FitID to your SKUs and lasts. Shoppers bring the fit data; you retain sales & catalog data.",
  },
  {
    icon: Shield,
    title: "Enterprise-grade",
    body: "SOC 2 Type II, GDPR, data-residency controls, SLA-backed uptime, SSO on request.",
  },
];

export default function ForCompanies() {
  return (
    <section
      id="for-companies"
      data-testid="for-companies-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14 md:mb-20">
          <div className="md:col-span-4">
            <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
              <span className="h-px w-8 bg-black/40" />
              For companies
            </div>
          </div>
          <h2
            data-testid="for-companies-headline"
            className="md:col-span-8 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            The consumer brings the data.{" "}
            <span className="text-neutral-400">You map it to your catalog.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Code snippet */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            data-testid="b2b-code-card"
            className="lg:col-span-6 rounded-2xl bg-[#0A0A0A] text-white overflow-hidden border border-black/10"
          >
            <div className="flex items-center justify-between px-5 h-11 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-white/20" />
                <span className="h-2 w-2 rounded-full bg-white/20" />
                <span className="h-2 w-2 rounded-full bg-white/20" />
              </div>
              <div className="text-[11px] font-mono text-white/40">product.html</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-white/40">
                cURL · JS
              </div>
            </div>
            <pre className="font-mono text-[12.5px] leading-[1.7] p-6 text-white/85 overflow-x-auto">
{`# Brand-accurate size for a shopper who already has a FitID
curl -X POST https://api.getfittin.com/api/recommend \\
  -H "Content-Type: application/json" \\
  -d '{
    "email":  "shopper@example.com",
    "brand":  "adidas",
    "model":  "Samba OG"
  }'

# → {
#     "recommended_eu_size": 43,
#     "size_display": "43",
#     "size_us_display": "10", "size_uk_display": "9½",
#     "confidence": "High",
#     "source": "brand_chart",
#     "available_widths": ["D"],
#     "reasoning": "Fits a 27.0 cm foot in the 26.8–27.4 cm bracket."
#   }`}
            </pre>
          </motion.div>

          {/* Feature grid */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`b2b-feature-${i + 1}`}
                className="rounded-2xl border border-black/10 bg-white p-6 hover:-translate-y-1 transition-all"
              >
                <f.icon className="h-5 w-5 text-[#0A0A0A]" strokeWidth={1.5} />
                <div className="mt-5 text-[16px] tracking-tight font-medium">
                  {f.title}
                </div>
                <p className="mt-2 text-[13.5px] text-neutral-600 leading-relaxed">
                  {f.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Retailer MCP outlook (iter14) */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          data-testid="retailer-mcp-outlook"
          className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center rounded-2xl bg-[#0A0A0A] text-white p-8 md:p-10 overflow-hidden relative"
        >
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-[10.5px] tracking-[0.22em] uppercase text-white/80">
              <Sparkles className="h-3 w-3" />
              Coming soon · Retailer MCP
            </div>
            <h3 className="mt-4 text-3xl md:text-4xl tracking-[-0.03em] font-medium leading-[1.05]">
              Answer individual fit questions <span className="text-white/50">inside your own chatbot.</span>
            </h3>
            <p className="mt-4 max-w-lg text-[14px] text-white/60 leading-relaxed">
              Embed Fittin as an MCP tool inside your retailer chatbot. When a shopper asks
              &quot;Do these run wide?&quot; or &quot;What&apos;s my size in the 990v6?&quot;, the assistant answers
              using their FitID and your catalog&apos;s chart — no generic advice.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-white/40">
                <Bot className="h-3.5 w-3.5" />
                Shop chat · New Balance
              </div>
              <div className="mt-4 space-y-3">
                <div className="rounded-xl bg-white/[0.04] px-3 py-2 text-[13px] text-white/80 max-w-[85%]">
                  Do these run wide?
                </div>
                <div className="rounded-xl bg-white text-[#0A0A0A] px-3 py-2 text-[13px] max-w-[85%] ml-auto font-medium">
                  For your foot (27.0 cm, wide fit), the 990v6 fits best in <span className="font-mono">EU 43 · 2E</span>.
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* B2B CTA strip */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-2xl border border-black/10 bg-white p-8"
        >
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-500">
              Talk to us
            </div>
            <div className="mt-2 text-2xl sm:text-3xl tracking-tight font-medium">
              Pilot Fittin with your catalog.
            </div>
            <div className="mt-1 text-[14px] text-neutral-600">
              30-day paid pilot · onboarding in under a week.
            </div>
          </div>
          <a
            href="#final-cta"
            data-testid="b2b-pilot-cta"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#final-cta");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="group inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-5 py-3 text-[14px] hover:bg-neutral-800 transition-all active:scale-[0.98] self-start md:self-auto"
          >
            Book a pilot
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
